import React, { useMemo, useState } from "react";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";

import LoadableButton from "src/components/LoadableButton";
import {
  ERROR_ALERT_SEARCH_FIELDS,
  getErrorAlertFilterValueLabel,
  type ErrorAlertSearchField,
  type SupportErrorAlertSearchFilter,
} from "src/interfaces/supportErrorAlerts.interface";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  field: {
    minWidth: 180,
  },
  valueField: {
    minWidth: 220,
    flex: "1 1 220px",
  },
  selectValueField: {
    minWidth: 280,
    flex: "1 1 280px",
  },
  addButton: {
    flexShrink: 0,
    height: 40,
  },
}));

interface ErrorAlertsSearchFiltersProps {
  filters: SupportErrorAlertSearchFilter[];
  onApply: (filters: SupportErrorAlertSearchFilter[]) => void;
  applying?: boolean;
}

function defaultDraftValue(field: ErrorAlertSearchField) {
  return field.valueOptions?.[0]?.value ?? "";
}

function ErrorAlertsSearchFilters({
  filters,
  onApply,
  applying = false,
}: ErrorAlertsSearchFiltersProps) {
  const classes = useStyles();
  const [draftField, setDraftField] = useState<ErrorAlertSearchField>(
    ERROR_ALERT_SEARCH_FIELDS[0]
  );
  const [draftValue, setDraftValue] = useState(() =>
    defaultDraftValue(ERROR_ALERT_SEARCH_FIELDS[0])
  );

  const fieldLabelById = useMemo(
    () =>
      Object.fromEntries(
        ERROR_ALERT_SEARCH_FIELDS.map((field) => [field.id, field.label])
      ),
    []
  );

  const addFilter = () => {
    const value = draftValue.trim();
    if (!value) return;

    const nextFilters = [
      ...filters.filter((filter) => filter.field !== draftField.id),
      {
        field: draftField.id,
        value,
      },
    ];

    onApply(nextFilters);
    setDraftValue(defaultDraftValue(draftField));
  };

  const removeFilter = (index: number) => {
    onApply(filters.filter((_, chipIndex) => chipIndex !== index));
  };

  const clearFilters = () => {
    onApply([]);
  };

  const valueControl = draftField.valueOptions ? (
    <TextField
      select
      label="Value"
      variant="outlined"
      size="small"
      margin="dense"
      className={classes.selectValueField}
      value={draftValue}
      disabled={applying}
      onChange={(event) => setDraftValue(event.target.value)}
      SelectProps={{ native: true }}
    >
      {draftField.valueOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </TextField>
  ) : (
    <TextField
      label="Value"
      variant="outlined"
      size="small"
      margin="dense"
      className={classes.valueField}
      value={draftValue}
      disabled={applying}
      name="error-alerts-filter-value"
      onChange={(event) => setDraftValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          addFilter();
        }
      }}
      inputProps={{
        autoComplete: "off",
        "data-lpignore": "true",
        "data-1p-ignore": "true",
      }}
    />
  );

  return (
    <Box display="flex" flexDirection="column" gridGap={8} flex={1}>
      <Box className={classes.row}>
        <Autocomplete
          options={ERROR_ALERT_SEARCH_FIELDS}
          getOptionLabel={(option) => option.label}
          value={draftField}
          disabled={applying}
          onChange={(_, value) => {
            if (!value) return;
            setDraftField(value);
            setDraftValue(defaultDraftValue(value));
          }}
          disableClearable
          className={classes.field}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Field"
              variant="outlined"
              size="small"
              margin="dense"
            />
          )}
        />
        {valueControl}
        <LoadableButton
          variant="outlined"
          color="primary"
          className={classes.addButton}
          loading={applying}
          disabled={applying || !draftValue.trim()}
          onClick={addFilter}
        >
          Add filter
        </LoadableButton>
      </Box>

      {filters.length > 0 ? (
        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {filters.map((filter, index) => (
            <Chip
              key={`${filter.field}-${filter.value}-${index}`}
              label={`${
                fieldLabelById[filter.field] || filter.field
              }: ${getErrorAlertFilterValueLabel(
                filter.field as ErrorAlertSearchField["id"],
                filter.value
              )}`}
              onDelete={applying ? undefined : () => removeFilter(index)}
            />
          ))}
          <Button
            size="small"
            color="secondary"
            disabled={applying}
            onClick={clearFilters}
          >
            Clear all
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

export default ErrorAlertsSearchFilters;
